import { startTransition, lazy, Suspense } from "react";
import { ArrowRight, FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLocalSession } from "@/hooks/useLocalSession";

const TryConverter = lazy(() => import("@/components/TryConverter"));

const ConverterSkeleton = () => (
  <div className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
    {[0, 1].map((i) => (
      <div
        key={i}
        className="rounded-[24px] border border-[#1e0d01]/10 bg-white h-72 animate-pulse"
      />
    ))}
  </div>
);

const Hero = () => {
  const navigate = useNavigate();
  const isLoggedIn = useLocalSession();

  return (
    <section className="relative overflow-hidden bg-white px-4 pt-[100px] pb-[80px] w-full">
      {/* Solar grid background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="relative max-w-[1280px] mx-auto h-full">
          {/* Vertical dashed lines */}
          <div className="absolute top-0 bottom-0 left-[20%] border-l border-dashed border-[#1e0d01]/10" />
          <div className="absolute top-0 bottom-0 left-[40%] border-l border-dashed border-[#1e0d01]/10" />
          <div className="absolute top-0 bottom-0 left-[60%] border-l border-dashed border-[#1e0d01]/10" />
          <div className="absolute top-0 bottom-0 left-[80%] border-l border-dashed border-[#1e0d01]/10" />
          {/* Horizontal dashed lines */}
          <div className="absolute top-[100px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1e0d01]/10 to-transparent" />
          <div className="absolute top-[380px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1e0d01]/10 to-transparent" />
          {/* Intersection dots */}
          <div className="absolute top-[98px] left-[20%] w-1.5 h-1.5 bg-[#1e0d01]/20 rounded-full -translate-x-[3px]" />
          <div className="absolute top-[98px] left-[40%] w-1.5 h-1.5 bg-[#1e0d01]/20 rounded-full -translate-x-[3px]" />
          <div className="absolute top-[98px] left-[60%] w-2 h-2 bg-primary/30 blur-[2px] rounded-full -translate-x-[4px]" />
          <div className="absolute top-[98px] left-[80%] w-1.5 h-1.5 bg-[#1e0d01]/20 rounded-full -translate-x-[3px]" />
          <div className="absolute top-[378px] left-[40%] w-1.5 h-1.5 bg-[#1e0d01]/20 rounded-full -translate-x-[3px]" />
          <div className="absolute top-[378px] left-[60%] w-2 h-2 bg-primary/30 blur-[2px] rounded-full -translate-x-[4px]" />
        </div>
      </div>

      {/* Ambient orange glow */}
      <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-primary/5 blur-[120px] rounded-full" />

      <div className="mx-auto max-w-[1160px] px-[30px] relative z-10 text-center">
        {/* Live badge */}
        <div className="mb-6 hero-fade-in" style={{ animationDelay: "0ms" }}>
          <span className="inline-flex items-center gap-2.5 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-medium text-primary">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-primary animate-pulse-slow" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-primary" />
            </span>
            PDF parsed in your browser
          </span>
        </div>

        {/* Headline */}
        <h1
          className="mb-5 font-medium hero-fade-in"
          style={{
            animationDelay: "80ms",
            fontSize: "clamp(2.5rem, 6vw, 4rem)",
            lineHeight: 1.05,
            letterSpacing: "-2.5px",
            color: "#1e0d01",
          }}
        >
          Turn messy bank PDFs into{" "}
          <br className="hidden sm:block" />
          <span style={{ color: "hsl(var(--primary))" }}>clean Excel spreadsheets</span>
        </h1>

        {isLoggedIn ? (
          <div
            className="flex flex-col items-center justify-center gap-4 hero-fade-in"
            style={{ animationDelay: "160ms" }}
          >
            <button
              className="group inline-flex items-center gap-3 rounded-full bg-primary text-white px-8 py-4 text-base font-semibold hover:scale-105 active:scale-95 transition-transform shadow-cta"
              onClick={() => startTransition(() => navigate("/dashboard"))}
            >
              <FolderOpen className="h-4 w-4" />
              Go to Dashboard
              <span className="w-6 h-6 overflow-hidden flex items-center justify-center">
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        ) : (
          <>
            {/* Subtitle */}
            <p
              className="mx-auto mb-4 max-w-[630px] text-[18px] leading-[28px] hero-fade-in"
              style={{
                animationDelay: "160ms",
                color: "rgba(30,13,1,0.6)",
              }}
            >
              Stop copying transactions by hand. Drop a PDF below — no signup needed.
            </p>

            {/* Trust bar */}
            <div
              className="flex items-center justify-center gap-3 text-xs mb-8 hero-fade-in"
              style={{ animationDelay: "240ms", color: "rgba(30,13,1,0.4)" }}
            >
              <span>Parsed locally</span>
              <span>·</span>
              <span>No signup to preview</span>
              <span>·</span>
              <span>5 free conversions</span>
            </div>

            {/* Glass-wrapped converter */}
            <div
              className="text-left animate-float-slow hero-fade-in"
              style={{ animationDelay: "320ms" }}
            >
              <div className="p-3 bg-white/20 rounded-[24px] backdrop-blur-md border border-[#1e0d01]/5">
                <div className="rounded-[12px] border border-[#1e0d01]/10 bg-[#fbfbfc] overflow-hidden">
                  <Suspense fallback={<ConverterSkeleton />}>
                    <TryConverter />
                  </Suspense>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Hero;
